import React, { useEffect, useState } from 'react';
import './ReelsGuide.css';

const DIAGNOSE_STEPS = [
  { id: 'eligible', step: '1', label: 'Was it eligible?' },
  { id: 'reach', step: '2', label: 'Who saw it?' },
  { id: 'leave', step: '3', label: 'When did they leave?' },
  { id: 'act', step: '4', label: 'Did they act?' },
] as const;

const MEDIA = [
  { id: 'watch', label: 'Watch' },
  { id: 'listen', label: 'Listen' },
  { id: 'read', label: 'Read' },
] as const;

const TOC = [
  { id: 'confirmed', label: 'What Instagram has confirmed' },
  { id: 'metrics', label: 'Metric changes' },
  { id: 'hypothesis', label: 'What a marketing hypothesis is' },
  { id: 'baseline', label: 'Build an honest baseline' },
  { id: 'trial', label: 'Trial Reels' },
  { id: 'stories', label: 'Stories' },
  { id: 'mistakes', label: 'Common mistakes' },
  { id: 'support', label: 'Support' },
] as const;

const OBSERVED_IDS = [
  'confirmed',
  'metrics',
  'diagnose',
  ...DIAGNOSE_STEPS.map((item) => item.id),
  'hypothesis',
  'baseline',
  'trial',
  'stories',
  'mistakes',
  ...MEDIA.map((item) => item.id),
  'support',
] as const;

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function headingForId(id: string): HTMLElement | null {
  const el = document.getElementById(id);
  if (!el) {
    return null;
  }
  if (el.matches('h2, h3')) {
    return el;
  }
  const heading = el.querySelector('h2, h3');
  return heading instanceof HTMLElement ? heading : el;
}

const ReelsGuide: React.FC = () => {
  const [activeId, setActiveId] = useState<string>(TOC[0].id);

  useEffect(() => {
    const targets = OBSERVED_IDS.map((id) => {
      const node = headingForId(id);
      return node ? { id, node } : null;
    }).filter((item): item is { id: (typeof OBSERVED_IDS)[number]; node: HTMLElement } => item !== null);

    if (targets.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const idByNode = new Map(targets.map((item) => [item.node, item.id]));
    const intersecting = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = idByNode.get(entry.target as HTMLElement);
          if (!id) {
            return;
          }
          if (entry.isIntersecting) {
            intersecting.add(id);
          } else {
            intersecting.delete(id);
          }
        });
        const current = [...OBSERVED_IDS].reverse().find((id) => intersecting.has(id));
        if (current) {
          setActiveId(current);
        }
      },
      { rootMargin: '0px 0px -78% 0px', threshold: 0 },
    );

    targets.forEach((item) => observer.observe(item.node));
    return () => observer.disconnect();
  }, []);

  const jumpTo = (id: string) => () => {
    setActiveId(id);
  };

  return (
    <article className="reels-guide">
      <div className="reels-guide-shell">
        <div className="reels-guide-main">
          <header className="reels-guide-hero">
            <h1>How to read a Reel</h1>
            <p className="reels-guide-lede">
              Open Insights. Work through this page in order. One number is not a diagnosis.
            </p>
            <div className="reels-guide-path">
              <svg className="reels-guide-path-line" viewBox="0 0 640 148" preserveAspectRatio="none" aria-hidden="true">
                <polyline
                  points="22,16 186,36 378,58 618,84"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  vectorEffect="nonScalingStroke"
                />
              </svg>
              <ol>
                {DIAGNOSE_STEPS.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span className="reels-guide-path-step">{item.step}</span>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
            <p className="reels-guide-updated">
              Researched 7 September 2026. Instagram changes metrics and ranking often — re-check the official links before treating this as current in 2027.
            </p>
          </header>

          <section id="confirmed">
            <h2>What Instagram has actually confirmed</h2>
            <ul>
              <li>
                Instagram uses different ranking systems for Feed, Stories, Explore and Reels. There is no single algorithm. Reels is mainly a discovery surface and often shows content from accounts the viewer does not follow.{' '}
                <Ext href="https://about.instagram.com/blog/announcements/instagram-ranking-explained">Instagram Ranking Explained</Ext>
              </li>
              <li>
                An eligible Reel is shown to a small audience Instagram expects may enjoy it, including non-followers, and expands if the early response is promising. Instagram has never published a fixed test-pool size. “Every Reel is tested on 200 people” is invented.{' '}
                <Ext href="https://creators.instagram.com/blog/recommendations-and-originality">Recommendations and originality</Ext>
                {' and '}
                <Ext href="https://creators.instagram.com/blog/helping-creators-of-all-sizes-break-through">Breaking through</Ext>
              </li>
              <li>
                Public ranking material names several predictions and signals, including watch-through, early skips, resharing, likes, comments, follows and audio use. Instagram does not publish their weights. Mosseri’s practical summary for creators is average watch time, likes per reach and sends per reach. Likes matter somewhat more for connected reach and sends for unconnected reach. That does not make sends a universal first signal.{' '}
                <Ext href="https://transparency.meta.com/features/explaining-ranking/ig-reels-chaining/">Reels ranking card</Ext>
                {' and '}
                <Ext href="https://www.instagram.com/mosseri/reel/DGA489oxVLM/">Mosseri on Insights</Ext>
              </li>
              <li>
                To reach strangers, Instagram recommends making each Reel understandable without prior knowledge and giving people a reason to stay in the first three seconds.{' '}
                <Ext href="https://creators.instagram.com/blog/helping-creators-of-all-sizes-break-through">Breaking through</Ext>
              </li>
              <li>
                Recommendation eligibility is a separate gate from content quality. A public account can still reach followers while being ineligible for recommendations. Check Account Status before blaming the hook.{' '}
                <Ext href="https://creators.instagram.com/blog/instagram-recommendations-eligibility-tips-creators">Recommendations eligibility</Ext>
              </li>
            </ul>
          </section>

          <section id="metrics">
            <h2>Metric changes and availability</h2>
            <p>
              Instagram began making <strong>Views</strong> the headline metric in 2024. On 21 April 2025, Meta retired the legacy API metrics Impressions and Plays in favour of Views. Reels Views include replays; Reach/Viewers is the unique-account measure. Do not compare old Plays or Impressions directly with current Views.
            </p>
            <p>
              In 2025–2026 Instagram also rolled out a retention chart, Skip Rate/View Rate, Share Rate and Views Over Time. Exact names and layout vary by account, app version and content type. Trial, collab, cross-posted and boosted Reels may use a different panel. Use the metric that is actually visible in the app.
            </p>
            <ul>
              <li><Ext href="https://creators.instagram.com/grow/insights">Official guide to Instagram Insights</Ext></li>
              <li><Ext href="https://help.instagram.com/2058621927791506">Official Reel metric definitions</Ext></li>
              <li><Ext href="https://help.instagram.com/202865988324236/">Official post and Reel Insights</Ext></li>
              <li><Ext href="https://www.socialmediatoday.com/news/instagram-improves-insights-ui-adds-new-metrics/818504/">April 2026 Insights interface report</Ext></li>
            </ul>
          </section>

          <section id="diagnose">
            <h2>Diagnose in this order</h2>
            <p>
              Do not infer a cause from one number. Read eligibility, then reach mix, then the first three seconds, then the rest of retention, then actions per reach, then conversion.
            </p>

            <h3 id="eligible">1. Was the content eligible?</h3>
            <p>Open Settings, then Account Status.</p>
            <p>
              Recommendation reach can be limited by guideline issues, reused or unoriginal content, visible watermarks, low-quality exports and some other content attributes. Reels longer than three minutes are not eligible for recommendations. Fix or appeal an eligibility problem before testing creative changes.
            </p>
            <p>
              Instagram’s 2024 rule made accounts posting 10 or more materially unaltered copies in 30 days ineligible for recommendations. Its 2026 wording is broader: accounts that primarily post unoriginal photos, carousels or Reels can be ineligible, with recovery based on a rolling 30-day period of mostly original work. Native Story reshares are the method Instagram itself recommends for passing on someone else’s post; they are not the feed/Reels aggregation behaviour targeted here.
            </p>
            <ul>
              <li><Ext href="https://creators.instagram.com/blog/recommendations-and-originality">Originality and the 2024 distribution changes</Ext></li>
              <li><Ext href="https://creators.instagram.com/blog/rewarding-original-creators-on-instagram">2026 original-content update</Ext></li>
              <li><Ext href="https://creators.instagram.com/original-content-guidelines">Original Content Guidelines</Ext></li>
            </ul>

            <h3 id="reach">2. Did it reach followers, strangers, or both?</h3>
            <p>Use Reach/Viewers, not Views, and open the follower/non-follower breakdown when available.</p>
            <div className="reels-guide-table-wrap">
              <table>
                <caption>Reach mix</caption>
                <thead>
                  <tr>
                    <th scope="col">Pattern</th>
                    <th scope="col">What it supports — not proves</th>
                    <th scope="col">Next check</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Low reach and almost no non-followers</td>
                    <td>Eligibility problem, or the initial recommendation test did not expand</td>
                    <td>Account Status first; then early skip and watch behaviour</td>
                  </tr>
                  <tr>
                    <td>Good follower reach, weak non-follower reach</td>
                    <td>The content may rely on context, or strangers did not respond as well as followers</td>
                    <td>Compare follower/non-follower engagement and ask whether the Reel stands alone</td>
                  </tr>
                  <tr>
                    <td>Strong non-follower reach</td>
                    <td>Instagram found a discovery audience</td>
                    <td>Check whether that attention produced watch time, shares, visits or follows</td>
                  </tr>
                  <tr>
                    <td>Many Views but much lower Reach</td>
                    <td>Replays or repeat displays, not broad discovery</td>
                    <td>Treat as depth of consumption, not audience growth</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Low non-follower reach does not prove that Instagram never distributed the Reel. Ordinary eligible Reels already receive a small cold-audience test; the pool size and exact expansion rules are unpublished.
            </p>

            <h3 id="leave">3. Where did viewers leave?</h3>
            <p>
              The retention graph is descriptive, not causal: it shows <em>when</em> the remaining audience left, not why.
            </p>
            <div className="reels-guide-table-wrap">
              <table>
                <caption>Retention</caption>
                <thead>
                  <tr>
                    <th scope="col">Pattern</th>
                    <th scope="col">Most useful interpretation</th>
                    <th scope="col">What to inspect</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>High Skip Rate or an unusually steep 0–3s fall</td>
                    <td>The opening failed to earn attention from this audience</td>
                    <td>First frame, first sentence, visual clarity, captions, promise and audience match</td>
                  </tr>
                  <tr>
                    <td>Opening holds, then one sharp fall</td>
                    <td>Something at that moment lost people</td>
                    <td>Pacing, tangent, repetition, confusing transition or delayed payoff</td>
                  </tr>
                  <tr>
                    <td>Gradual decline throughout</td>
                    <td>Normal attrition; the Reel may simply be longer than the value supports</td>
                    <td>Compare with our Reels of similar length</td>
                  </tr>
                  <tr>
                    <td>Retention rises or flattens around one moment</td>
                    <td>Rewatching or a particularly useful or unclear section</td>
                    <td>Preserve the useful moment; check whether it needed replay because it was confusing</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Good reach with poor retention means the Reel received distribution but did not hold that audience well. Call it a hook problem only when the weakness is concentrated in the first three seconds. A mid-Reel drop is a body or payoff problem. Weak average watch time alone cannot distinguish them.
            </p>

            <h3 id="act">4. Did viewers find it valuable enough to act?</h3>
            <p>Calculate rates against Reach/Viewers where possible:</p>
            <ul>
              <li><strong>Share rate = shares ÷ reach.</strong> Suggests the Reel was worth passing on. “Shares” may combine DMs, Story shares and other routes; do not silently rename all shares “sends.”</li>
              <li><strong>Save rate = saves ÷ reach.</strong> Suggests reference value: something worth returning to.</li>
              <li><strong>Like rate = likes ÷ reach.</strong> Lightweight approval, particularly useful for follower response.</li>
              <li><strong>Follow rate = follows from the Reel ÷ reach.</strong> The cleanest per-Reel growth outcome.</li>
              <li><strong>Profile-visit rate = profile visits ÷ reach.</strong> Interest in the creator beyond the post.</li>
            </ul>
            <div className="reels-guide-table-wrap">
              <table>
                <caption>Retention and actions</caption>
                <thead>
                  <tr>
                    <th scope="col">Retention and actions</th>
                    <th scope="col">Plausible reading</th>
                    <th scope="col">Better next move</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Watch holds; shares and saves are weak</td>
                    <td>Watchable but not useful, distinctive or pass-on-worthy</td>
                    <td>Test a stronger topic, takeaway or social use</td>
                  </tr>
                  <tr>
                    <td>Saves strong; shares weak</td>
                    <td>Useful as personal reference, not naturally sendable</td>
                    <td>Keep if authority or teaching is the goal</td>
                  </tr>
                  <tr>
                    <td>Shares strong; saves weak</td>
                    <td>Socially relevant or entertaining, not a reference tool</td>
                    <td>Keep if discovery is the goal</td>
                  </tr>
                  <tr>
                    <td>Shares and watch strong; follows weak</td>
                    <td>Reach succeeded but creator relevance or profile conversion may be weak</td>
                    <td>Check profile visits, bio, pinned posts and whether the audience is actually a prospective student</td>
                  </tr>
                  <tr>
                    <td>Profile visits strong; follows or enquiries weak</td>
                    <td>The profile or offer may not answer why follow or contact Veronica</td>
                    <td>Review bio, grid, Highlights and the call to action</td>
                  </tr>
                  <tr>
                    <td>Likes strong only among followers</td>
                    <td>Existing community liked it; this says little about discovery</td>
                    <td>Treat as community content, not failed growth content</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>These patterns narrow the possibilities. None identifies a root cause by itself.</p>
          </section>

          <section id="hypothesis">
            <h2>What a marketing hypothesis is</h2>
            <p>
              A marketing hypothesis is a specific, falsifiable prediction about how one deliberate change should affect one audience behaviour. It turns posting from random activity into learning.
            </p>
            <p>Use this form:</p>
            <blockquote>
              <p>
                If we change [one thing] for [this audience], then [one metric] should move [in this direction], because [reason]. We will judge it across [a defined batch or time period] against [our baseline].
              </p>
            </blockquote>
            <p>Example:</p>
            <blockquote>
              <p>
                If Veronica opens a cold-audience grammar Reel with the learner’s mistake rather than “Today I’ll explain…”, then first-three-second hold should improve, because the viewer recognises their problem before being asked to listen. Compare the next five similar 20–30 second Reels with the median of the previous ten.
              </p>
            </blockquote>
            <p>A useful hypothesis has:</p>
            <ol>
              <li><strong>One meaningful variable.</strong> Do not change the topic, hook, length and editing style together.</li>
              <li><strong>One primary metric.</strong> A hook hypothesis uses early hold or Skip Rate, not follower growth.</li>
              <li><strong>A comparison.</strong> Prefer our own median for the same format and length range.</li>
              <li><strong>Enough repetitions.</strong> One Reel is noisy evidence, not a conclusion.</li>
              <li><strong>A decision.</strong> Keep, reject or revise the idea after the batch.</li>
            </ol>
            <p>
              Facts describe Instagram’s system. Insights describe what happened. A hypothesis predicts what a controlled change will do next. Keep those three layers separate.
            </p>
          </section>

          <section id="baseline">
            <h2>Build an honest baseline</h2>
            <p>
              There is no official “good” retention, Skip Rate, Share Rate or non-follower-reach percentage. Most confident universal thresholds online are invented or taken from an unrelated account.
            </p>
            <p>Use Veronica’s rolling median:</p>
            <ul>
              <li>last 20–30 organic Reels, with a smaller recent window only if the format changed substantially;</li>
              <li>compare similar length bands and purposes;</li>
              <li>separate ordinary, Trial, collab and boosted Reels;</li>
              <li>compare Reels with Reels, Stories with Stories and carousels with carousels;</li>
              <li>keep the median, not the average, so one viral post does not distort normal performance.</li>
            </ul>
            <p>
              <Ext href="https://www.socialinsider.io/social-media-benchmarks/instagram">Socialinsider’s 35M-post 2026 benchmark</Ext> is useful for broad context, but its engagement rate uses followers as the denominator and should not become a pass/fail threshold.
            </p>
          </section>

          <section id="trial">
            <h2>Trial Reels</h2>
            <p>
              <Ext href="https://creators.instagram.com/blog/instagram-trial-reels">Trial Reels</Ext> let a professional account show a Reel to non-followers first without placing it on the main grid or in followers’ Feed or Reels tab. Initial insights appear after roughly 24 hours; optional auto-sharing uses views during the first 72 hours.
            </p>
            <p>
              Use them to test unfamiliar formats or stranger-facing versions of community content without filling the grid. Do not call them a laboratory test:
            </p>
            <ul>
              <li>Instagram is still choosing the audience;</li>
              <li>some followers can encounter the Reel through DMs or audio, location or filter pages;</li>
              <li>Trial and ordinary Reels are distributed differently;</li>
              <li>the 72-hour views rule is Instagram’s auto-share rule, not an official definition of quality.</li>
            </ul>
          </section>

          <section id="stories">
            <h2>Stories: read behaviour, not verdicts</h2>
            <p>
              <Ext href="https://help.instagram.com/383939598845756/">Official Story Insights definitions</Ext>
            </p>
            <div className="reels-guide-table-wrap">
              <table>
                <caption>Story signals</caption>
                <thead>
                  <tr>
                    <th scope="col">Signal</th>
                    <th scope="col">Careful interpretation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Taps back</td>
                    <td>May mean rereading, interest, confusion or an accidental tap. Useful only relative to our normal rate</td>
                  </tr>
                  <tr>
                    <td>Taps forward</td>
                    <td>Normal Story browsing. A frame-level spike may indicate slow, repetitive or irrelevant content</td>
                  </tr>
                  <tr>
                    <td>Exits</td>
                    <td>Position matters: an exit on the last frame can simply mean completion</td>
                  </tr>
                  <tr>
                    <td>Next story</td>
                    <td>The viewer moved to another account; negative for this sequence, but not an official ranking penalty</td>
                  </tr>
                  <tr>
                    <td>Replies / sticker taps</td>
                    <td>Higher-effort interaction and useful evidence of active interest</td>
                  </tr>
                  <tr>
                    <td>Approximate completion</td>
                    <td>Last-frame viewers divided by first-frame viewers. Directional only, because people can enter mid-sequence</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              For TeachTalk, community milestones still belong primarily in Stories: existing followers understand the context and tagged students can reshare natively. That is a content-audience fit decision, not a claim that Stories are a growth channel.
            </p>
          </section>

          <section id="mistakes">
            <h2>Common mistakes</h2>
            <ul className="reels-guide-mistakes">
              <li>Judging a Reel from Views alone.</li>
              <li>Dividing single-post engagement by total followers instead of Reach/Viewers.</li>
              <li>Calling every early drop a hook problem.</li>
              <li>Comparing different formats, lengths, purposes or paid and organic distribution.</li>
              <li>Treating correlation as a published algorithm weight.</li>
              <li>Changing several creative variables at once.</li>
              <li>Declaring a hypothesis proven or disproven from one post.</li>
              <li>Mixing pre-2025 Plays or Impressions with current Views.</li>
            </ul>
          </section>

          <section id="resources">
            <h2>Watch, listen, read</h2>
            <h3 id="watch">Watch</h3>
            <ol className="reels-guide-resources">
              <li>
                <Ext href="https://www.instagram.com/mosseri/reel/DGA489oxVLM/">Mosseri: the three Insights signals to watch</Ext>
                <span>Short, direct, and more useful than most courses.</span>
              </li>
              <li>
                <Ext href="https://www.youtube.com/watch?v=owjlqvPQffY">Buffer — Instagram has FOUR Algorithms: How Your Content is Ranked in 2026</Ext>
                <span>28 July 2026, 13 minutes. The best short overview: why Feed, Stories, Reels and Explore behave differently, plus originality and Account Status. Treat sends as one important signal, not the only one.</span>
              </li>
              <li>
                <Ext href="https://www.youtube.com/watch?v=UGYo_pYtl4c&t=490s">Jade Beason — Instagram’s NEW algorithm? IG Instants? YouTube’s New Navigation</Ext>
                <span>24 May 2026, 16 minutes. Watch 08:10–11:00 for Skip Rate, Share Rate and Views Over Time. The rest is unrelated platform news.</span>
              </li>
              <li>
                <Ext href="https://www.youtube.com/watch?v=HDzhA_UFrnA&t=590s">Colin and Samir — An honest conversation with the CEO of Instagram</Ext>
                <span>10 June 2024, 1 hour 32 minutes. Watch 09:50–24:20: a useful 15-minute explanation of reach, recommendations and sends. The philosophy remains useful; its Impressions/Plays language and interface details are dated.</span>
              </li>
              <li>
                <Ext href="https://www.youtube.com/watch?v=7afTeh_PQus&t=1639s">Semafor Mixed Signals — Adam Mosseri on Instagram, Algorithms, & How To Beat TikTok</Ext>
                <span>19 December 2025, 55 minutes. Watch 27:19–32:36 for the clearest explanation of exploration ranking: small tests, expansion and why reach is volatile. His round numbers are illustrations, not thresholds.</span>
              </li>
            </ol>
            <p>
              This list does not pad itself with generic “2026 algorithm” videos. Most short results either still teach deprecated Plays/Impressions or present unsupported retention thresholds as rules.
            </p>

            <h3 id="listen">Listen</h3>
            <ol className="reels-guide-resources">
              <li>
                <Ext href="https://open.spotify.com/episode/3VgmbufG5At9WtIrK8R0g5">Dinner with Sally — Instagram’s CEO reveals the Instagram algorithm & its secrets!</Ext>
                <span>44 minutes. Optional background listening about Instagram’s direction and the shift from Feed to Reels and DMs. The title oversells it: this is not a practical Insights tutorial.</span>
              </li>
            </ol>

            <h3 id="read">Read</h3>
            <ol className="reels-guide-resources">
              <li>
                <Ext href="https://about.instagram.com/blog/announcements/instagram-ranking-explained">Instagram Ranking Explained</Ext>
                <span>The evergreen explanation of why Feed, Stories, Explore and Reels behave differently.</span>
              </li>
              <li>
                <Ext href="https://transparency.meta.com/features/explaining-ranking/ig-reels-chaining/">How Instagram Reels ranking works</Ext>
                <span>Meta’s current technical list of Reels predictions, including early skips and watch behaviour.</span>
              </li>
              <li>
                <Ext href="https://creators.instagram.com/blog/helping-creators-of-all-sizes-break-through">Helping creators of all sizes break through</Ext>
                <span>Official advice on cold-audience distribution, standalone content and the first three seconds.</span>
              </li>
              <li>
                <Ext href="https://creators.instagram.com/grow/insights">Instagram Insights guide</Ext>
                <span>Use alongside the Insights dashboard.</span>
              </li>
            </ol>
          </section>

          <section id="support">
            <h2>Support</h2>
            <p>
              If the Insights panel uses different names from this page, trust the labels in the app. Trial, collab, cross-posted and boosted Reels often look different from an ordinary organic Reel.
            </p>
            <p>
              When a Reel looks “broken,” start with Account Status, then Reach/Viewers, then the retention graph, then rates against reach. Do not start with Views, and do not start with a YouTube video that quotes a 40% retention rule.
            </p>
            <p>
              If a number here disagrees with an official Instagram or Meta page, the official page wins. Re-check the Read links before changing how we read Insights in 2027.
            </p>
            <p>
              This page is a reference for reading our own stats. It is not a growth plan, and it does not replace the standing rule that community milestones belong in Stories.
            </p>
          </section>
        </div>

        <nav className="reels-guide-toc" aria-label="On this page">
          <div className="reels-guide-toc-quick">
            <p className="reels-guide-toc-title">Quick access</p>
            <a
              href="#diagnose"
              className={
                activeId === 'diagnose' ? 'reels-guide-toc-lead is-active' : 'reels-guide-toc-lead'
              }
              onClick={jumpTo('diagnose')}
            >
              Diagnose
            </a>
            <ol className="reels-guide-toc-diagnose">
              {DIAGNOSE_STEPS.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeId === item.id ? 'is-active' : undefined}
                    onClick={jumpTo(item.id)}
                  >
                    <span className="reels-guide-toc-step">{item.step}</span>
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
            <p className="reels-guide-toc-kicker">Watch / listen / read</p>
            <ul className="reels-guide-toc-media">
              {MEDIA.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeId === item.id ? 'is-active' : undefined}
                    onClick={jumpTo(item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <p className="reels-guide-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? 'is-active' : undefined}
                  onClick={jumpTo(item.id)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </article>
  );
};

export default ReelsGuide;
